export type FuzzyMatchResult = {
  /**
   * The rendered string with the fuzzy match highlighted
   */
  rendered: string;
  /**
   * The score of the fuzzy match
   */
  score: number;
};

export type FuzzyMatchOptions = {
  /**
   * The prefix to wrap around each matching character
   */
  pre?: string;
  /**
   * The suffix to wrap around each matching character
   */
  post?: string;
  /**
   * Whether to match case-sensitively
   */
  caseSensitive?: boolean;
};

// If `pattern` matches `str`, wrap each matching character
// in `opts.pre` and `opts.post`. If no match, return null
export default function fuzzyMatch(
  pattern: string,
  str: string,
  opts: FuzzyMatchOptions = {}
): FuzzyMatchResult | null {
  const { pre, post, caseSensitive = false } = opts;
  let patternIdx = 0;
  const result = [];
  const len = str.length;
  let totalScore = 0;
  let currScore = 0;
  const compareString = (caseSensitive && str) || str.toLowerCase();
  let ch;
  let lastMatched = false;
  let thisMatched = false;

  pattern = (caseSensitive && pattern) || pattern.toLowerCase();

  for (let idx = 0; idx < len; idx++) {
    ch = str[idx];
    lastMatched = thisMatched;
    thisMatched = false;

    if (compareString[idx] === pattern[patternIdx]) {
      patternIdx += 1;
      // consecutive characters should increase the score exponentially
      currScore += 1;
      thisMatched = true;
    } else {
      currScore = 0;
    }

    totalScore += currScore;

    if (post && lastMatched && !thisMatched) {
      result.push(post);
    }
    if (pre && !lastMatched && thisMatched) {
      result.push(pre);
    }

    result.push(ch);
  }

  if (post && thisMatched) {
    result.push(post);
  }

  // return rendered string if we have a match for every char
  if (patternIdx === pattern.length) {
    // if the string is an exact match with pattern, totalScore should be maxed
    totalScore = compareString === pattern ? Infinity : totalScore;
    return { rendered: result.join(""), score: totalScore };
  }

  return null;
}
